import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import { OccupiedPlacementKind } from '@dealstack/shared/lib/backendClient';
import type { OccupiedPlacementRes } from '@dealstack/shared/lib/backendClient';
import type {
	CanvasPlacement,
	FixedPlacement,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/SignaturePlacement/PlacementCanvas/PlacementCanvas.types';
import type {
	CounterpartyPlacementPayload,
	CounterpartySlot,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/AddCounterparty/useCounterpartyPlacement.types';

/** Canvas key for a slot ordinal. Disjoint from the saved editor's role-tuple keys. */
export const counterpartySlotKey = (signatureIndex: number): string =>
	`counterparty:${signatureIndex}`;

/**
 * The slots the new counterparty will have, in the order the backend creates them.
 *
 * Labels match how `enumerateSlots` names a counterparty's slots, so the panel here reads the
 * same as the template placement editor and the backend's rejection messages.
 */
export const buildCounterpartySlots = (
	partyName: string,
	slotCount: number
): CounterpartySlot[] => {
	const name = partyName.trim() || 'Counterparty';
	return Array.from({ length: slotCount }, (_, signatureIndex) => ({
		key: counterpartySlotKey(signatureIndex),
		signatureIndex,
		label: `${name} #${signatureIndex + 1}`,
		isWitness: false,
		roleType: DocumentSigningRoleType.COUNTERPARTY,
	}));
};

/** Drawn boxes as the API takes them, ordered by slot; slots with no box are left out. */
export const toPlacementPayload = (
	slots: CounterpartySlot[],
	placementsByKey: Map<string, CanvasPlacement>
): CounterpartyPlacementPayload[] =>
	slots.flatMap((slot) => {
		const placement = placementsByKey.get(slot.key);
		if (!placement) {
			return [];
		}
		return [
			{
				signatureIndex: slot.signatureIndex,
				pageNum: placement.pageNum,
				x: placement.pdfBox.x,
				y: placement.pdfBox.y,
				width: placement.pdfBox.width,
				height: placement.pdfBox.height,
			},
		];
	});

/** The live document's existing boxes, as read-only canvas boxes the new ones must keep clear of. */
export const toFixedPlacements = (boxes: OccupiedPlacementRes[]): FixedPlacement[] =>
	boxes.map((box) => ({
		key: box.key,
		label: box.label,
		kind: box.kind === OccupiedPlacementKind.DATE ? 'date' : 'signature',
		isWitness: box.isWitness,
		pageNum: box.pageNum,
		pdfBox: { x: box.x, y: box.y, width: box.width, height: box.height },
	}));
